import Observable from './Observable';
import Image from './Image';
import Audio from './Audio';
import AdaptrFeedFm from './AdaptrFeedFm';
import Video from './Video';
import WaveSurfer from './WaveSurfer';

export interface ResourceSourceType {
  id: string,
  type: 'image' | 'poster' | 'video' | 'audio' | 'waveform' | 'adaptrFeedFm' | 'unknown',
  url: string,
}

export interface IResourceItem {
  id: string,
  object: Audio | AdaptrFeedFm | Video | WaveSurfer | Image,
  status: 'loading' | 'error' | 'ready',
}

export interface IResource {
  load(_list: Array<ResourceSourceType>): void;
  get(_id: string): IResourceItem | undefined;
}

class Resource extends Observable implements Resource {
  public readonly EVENT = {
    ALL_RESOURCES_LOADED: 'ALL_RESOURCES_LOADED',
    RESOURCE_LOADED: 'RESOURCE_LOADED',
    RESOURCE_ERROR: 'RESOURCE_ERROR',
  };

  private readonly items: Array<IResourceItem> = [];

  public load(list: Array<ResourceSourceType>) {
    list.forEach((source) => {
      if (source.type === 'adaptrFeedFm') {
        this.loadAdaptrFeedFm(source);
      }
      if (source.type === 'image') {
        this.loadImage(source);
      }
      if (source.type === 'poster') {
        this.loadImage(source);
      }
      if (source.type === 'video') {
        this.loadVideo(source);
      }
      if (source.type === 'audio') {
        this.loadAudio(source);
      }
      if (source.type === 'waveform') {
        this.loadWaveform(source);
      }
    });
  }

  public get(id: string | undefined): IResourceItem | undefined {
    if (!id) {
      return undefined;
    }
    return this.items.find((item: IResourceItem) => (item.id === id));
  }

  private loadImage(source: ResourceSourceType): void {
    if (typeof document === 'undefined') {
      return;
    }
    const object: Image = new Image(source.id, source.url);
    const item: IResourceItem = {
      id: source.id,
      object,
      status: 'loading',
    };
    const self = this;
    function handleReady() {
      object.removeEventListener(Image.EVENT.LOAD, handleReady);
      object.removeEventListener(Image.EVENT.ERROR, handleError);
      item.status = 'ready';
      self.checkReady();
    }
    function handleError() {
      object.removeEventListener(Image.EVENT.LOAD, handleReady);
      object.removeEventListener(Image.EVENT.ERROR, handleError);
      item.status = 'error';
      self.checkReady();
    }
    object.addEventListener(Image.EVENT.LOAD, handleReady);
    object.addEventListener(Image.EVENT.ERROR, handleError);
    this.items.push(item);
    object.load();
  }

  private loadVideo(source: ResourceSourceType): void {
    if (typeof document === 'undefined') {
      return;
    }
    const object: Video = new Video(source.id, source.url, 20000);
    const container = (document.body.querySelector('[data-type="resource-temp-container"]') || document.createElement('div')) as HTMLDivElement;
    if (!container.hasAttribute('data-type')) {
      container.setAttribute('data-type', 'resource-temp-container');
      container.style.position = 'fixed';
      container.style.visibility = 'hidden';
      container.style.pointerEvents = 'none';
      container.style.width = '10px';
      document.body.appendChild(container);
    }
    container.appendChild(object.getElement());
    object.getElement();
    const item: IResourceItem = {
      id: source.id,
      object,
      status: 'loading',
    };
    const self = this;
    function handleReady() {
      object.removeEventListener(Video.EVENT.CAN_PLAY_THROUGH_FIRST_FRAME_READY, handleReady);
      object.removeEventListener(Video.EVENT.ERROR, handleError);
      item.status = 'ready';
      self.checkReady();
    }
    function handleError() {
      object.removeEventListener(Video.EVENT.CAN_PLAY_THROUGH_FIRST_FRAME_READY, handleReady);
      object.removeEventListener(Video.EVENT.ERROR, handleError);
      item.status = 'error';
      self.checkReady();
    }
    this.items.push(item);
    object.addEventListener(Video.EVENT.CAN_PLAY_THROUGH_FIRST_FRAME_READY, handleReady);
    object.addEventListener(Video.EVENT.ERROR, handleError);
    object.load();
  }

  private loadAdaptrFeedFm(source: ResourceSourceType): void {
    if (typeof document === 'undefined') {
      return;
    }
    const [,
      audioId,
      audioName,
      token,
      secret,
      requestParamName,
      requestParamValue,
    ] = source.url.match(/AdaptrFeedFm:\/\/([0-9]+?)\/([^?]+)\?token=([^&]*)&secret=([^&]*)&requestParamName=([^&]*)&requestParamValue=(.*)/) || [];
    const object: AdaptrFeedFm = new AdaptrFeedFm(
      source.id,
      Number(audioId),
      audioName,
      token
        ? { token, secret }
        : { requestParamName, requestParamValue },
    );
    const item: IResourceItem = {
      id: source.id,
      object,
      status: 'loading',
    };
    const self = this;
    function handleReady() {
      object.removeEventListener(AdaptrFeedFm.EVENT.LOAD, handleReady);
      object.removeEventListener(AdaptrFeedFm.EVENT.LOAD_ERROR, handleError);
      item.status = 'ready';
      self.checkReady();
    }
    function handleError(event: any) {
      object.removeEventListener(AdaptrFeedFm.EVENT.LOAD, handleReady);
      object.removeEventListener(AdaptrFeedFm.EVENT.LOAD_ERROR, handleError);
      item.status = 'error';
      self.checkReady();
      if (event?.payload?.error?.message) {
        console.error('ERROR LOAD AUDIO:', event?.payload?.error?.message, `source (${source.url})`);
      }
    }
    object.addEventListener(AdaptrFeedFm.EVENT.LOAD, handleReady);
    object.addEventListener(AdaptrFeedFm.EVENT.LOAD_ERROR, handleError);
    this.items.push(item);
    object.load();
  }

  private loadAudio(source: ResourceSourceType): void {
    if (typeof document === 'undefined') {
      return;
    }
    const object: Audio = new Audio(source.id, source.url, 4000);
    const item: IResourceItem = {
      id: source.id,
      object,
      status: 'loading',
    };
    const self = this;
    function handleReady() {
      object.removeEventListener(Audio.EVENT.LOAD, handleReady);
      object.removeEventListener(Audio.EVENT.LOAD_ERROR, handleError);
      item.status = 'ready';
      self.checkReady();
    }
    function handleError(event: any) {
      object.removeEventListener(Audio.EVENT.LOAD, handleReady);
      object.removeEventListener(Audio.EVENT.LOAD_ERROR, handleError);
      item.status = 'error';
      self.checkReady();
      if (event?.payload?.error?.message) {
        console.error('ERROR LOAD AUDIO:', event?.payload?.error?.message, `source (${source.url})`);
      }
    }
    object.addEventListener(Audio.EVENT.LOAD, handleReady);
    object.addEventListener(Audio.EVENT.LOAD_ERROR, handleError);
    this.items.push(item);
    object.load();
  }

  private loadWaveform(source: ResourceSourceType): void {
    if (typeof document === 'undefined') {
      return;
    }
    const object: WaveSurfer = new WaveSurfer(source.id);
    const item: IResourceItem = {
      id: source.id,
      object,
      status: 'loading',
    };
    const self = this;
    function handleReady() {
      object.removeEventListener(WaveSurfer.EVENT.READY, handleReady);
      object.removeEventListener(WaveSurfer.EVENT.ERROR, handleError);
      item.status = 'ready';
      self.checkReady();
    }
    function handleError() {
      object.removeEventListener(WaveSurfer.EVENT.READY, handleReady);
      object.removeEventListener(WaveSurfer.EVENT.ERROR, handleError);
      item.status = 'error';
      self.checkReady();
    }
    object.addEventListener(WaveSurfer.EVENT.READY, handleReady);
    object.addEventListener(WaveSurfer.EVENT.ERROR, handleError);
    this.items.push(item);
    object.load(source.url);
  }

  private checkReady(): void {
    let isReady = true;
    let loaded = 0;
    this.items.forEach((item) => {
      if (item.status === 'loading') {
        isReady = false;
      } else {
        loaded += 1;
      }
    });
    this.dispatchEvent(this.EVENT.RESOURCE_LOADED, {
      loaded,
      total: this.items.length,
    });
    if (isReady) {
      this.dispatchEvent(this.EVENT.ALL_RESOURCES_LOADED, {});
    }
  }
}

export default new Resource();
